import type {
  VhAdminAudience,
  VhAdminAudiencePayload,
  VhAdminCurrentUser,
  VhAdminFeature,
  VhAdminFeatureType,
  VhAdminGetAllPeopleParams,
  VhAdminGetVesselsParams,
  VhAdminGrantSubscriptionPayload,
  VhAdminServicePlan,
  VhAdminServiceProvider,
  VhAdminUsersPage,
  VhAdminVesselsPage,
} from '@/admin/admin.types';
import { API, deleteRequest, getRequest, patchRequest, postRequest, putRequest } from '@/api/api';

/** GET /user/me on AUTH — the only source of the system role (admin / super_admin). */
export const vhAdmGetCurrentUser = async (): Promise<VhAdminCurrentUser> => {
  const response = await getRequest<VhAdminCurrentUser>(API.AUTH, '/user/me');
  return response.data;
};

export const vhAdmGetAllPeople = async (
  params: VhAdminGetAllPeopleParams,
): Promise<VhAdminUsersPage> => {
  const response = await getRequest<VhAdminUsersPage>(API.PEOPLE, '/all', false, params);
  return response.data;
};

export const vhAdmSetPersonAudience = async (
  personId: string,
  audienceId: string,
): Promise<void> => {
  await patchRequest(API.PEOPLE, `/${personId}/beta-group`, { group_id: audienceId });
};

export const vhAdmGetBetaFeatures = async (): Promise<VhAdminFeature[]> => {
  const response = await getRequest<VhAdminFeature[]>(API.PEOPLE, '/beta-features');
  return response.data;
};

export const vhAdmMarkBetaFeature = async (
  feature: string,
  type: VhAdminFeatureType,
): Promise<void> => {
  await postRequest(API.PEOPLE, '/beta-features', { feature, type });
};

/** Unmarking a frontend feature also drops it from every audience holding it. */
export const vhAdmUnmarkBetaFeature = async (feature: string): Promise<void> => {
  await deleteRequest(API.PEOPLE, `/beta-features/${encodeURIComponent(feature)}`, undefined);
};

export const vhAdmGetAudiences = async (): Promise<VhAdminAudience[]> => {
  const response = await getRequest<VhAdminAudience[]>(API.PEOPLE, '/beta-features/groups');
  return response.data;
};

export const vhAdmCreateAudience = async (
  payload: VhAdminAudiencePayload,
): Promise<VhAdminAudience> => {
  const response = await postRequest<VhAdminAudience, VhAdminAudiencePayload>(
    API.PEOPLE,
    '/beta-features/groups',
    payload,
  );
  return response.data;
};

/** Replaces name *and* features — partial updates aren't a thing on this endpoint. */
export const vhAdmUpdateAudience = async (
  id: string,
  payload: VhAdminAudiencePayload,
): Promise<void> => {
  await putRequest(API.PEOPLE, `/beta-features/groups/${id}`, payload);
};

export const vhAdmDeleteAudience = async (id: string): Promise<void> => {
  await deleteRequest(API.PEOPLE, `/beta-features/groups/${id}`, undefined);
};

export const vhAdmGetVessels = async (
  params: VhAdminGetVesselsParams,
): Promise<VhAdminVesselsPage> => {
  const response = await getRequest<VhAdminVesselsPage>(API.VESSEL, '', false, params);
  return response.data;
};

/**
 * The live plan of each vessel, up to 100 at once — seat counts and whether an admin granted it,
 * which the vessel list doesn't carry. A vessel on no live plan is left out: it is on Harbor.
 */
export const vhAdmGetServicePlans = async (serviceIds: string[]): Promise<VhAdminServicePlan[]> => {
  const response = await getRequest<VhAdminServicePlan[]>(
    API.PEOPLE,
    '/subscriptions/services',
    false,
    { ids: serviceIds },
  );
  return response.data;
};

/** Name, logo and type of each provider, up to 100 at once; one that no longer exists is left out. */
export const vhAdmGetServiceProviders = async (
  ids: string[],
): Promise<VhAdminServiceProvider[]> => {
  // This client has no paramsSerializer, so an array would go out as ids[]=… and never bind.
  const params = new URLSearchParams(ids.map((id) => ['ids', id]));
  const response = await getRequest<VhAdminServiceProvider[]>(
    API.SERVICE_PROVIDERS,
    'service-provider/admin/info',
    false,
    params,
  );
  return response.data;
};

/** Replaces the vessel's plan with one that never expires, cancelling any live Stripe one first. */
export const vhAdmGrantSubscription = async (
  payload: VhAdminGrantSubscriptionPayload,
): Promise<void> => {
  await postRequest(API.PEOPLE, '/subscriptions/grant', payload);
};

/** Takes a granted lifetime plan back, leaving the vessel and everyone on it on Harbor. */
export const vhAdmRevokeSubscription = async (serviceId: string): Promise<void> => {
  await deleteRequest(API.PEOPLE, `/subscriptions/grant/${serviceId}`, undefined);
};
